import { AUDIENCE_BLOCK_REASON, AUDIENCE_MODE } from 'lib';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CampaignBuilder, { CampaignPreview } from '../components/CampaignBuilder';
import CampaignList from '../components/CampaignList';
import { focusRequiredField, missingCampaignFields } from '../components/campaignRequirements';
import ScheduleCalendar from '../components/ScheduleCalendar';
import TemplateTextEditor, { DraftBadge } from '../components/TemplateTextEditor';
import {
  Badge,
  Btn,
  Card,
  Failed,
  Loading,
  Note,
  PageBand,
  SectionHead,
  T
} from '../components/ui';
import VersionHistory from '../components/VersionHistory';
import { useEmailManager } from '../index';

/**
 * `/admin/email/setup` — Setup & Automation: write the email, aim it, and give it a time.
 *
 * The design's frame, and the order the work is actually done in: the template editor, the campaign
 * builder, the scheduler. These were two routes, which meant the template an operator had just
 * edited had to be chosen a second time on the next page to be sure it was the one being sent —
 * two controls for one decision, with a navigation in between.
 *
 * The audience is not here. It is the landing page, and while it shared this one the builder had to
 * give up the width, which left a 584px email rendering into roughly 440px.
 *
 * ## The preview lives in the builder
 *
 * Not as a card of its own beside both, which is what it was. The design puts it inside the
 * Campaign Builder, to the right of the fields — and that is the pairing that does the work: the
 * campaign name, the template and the audience are what change what the preview shows, so the
 * answer sits next to the question rather than in a third card that had to be scrolled to.
 *
 * The template editor above no longer has it alongside. That is the trade, and it is the right way
 * round: the editor's own Preview button still renders into this pane, and the editor is used once
 * per template where the builder is used once per campaign.
 *
 * The pane used to draw the email at its true 680px and scroll when it did not fit, which is why
 * it needed a full page column at all. It scales to fit now and says what percentage it is
 * showing, so it can live inside a card and still show a whole email.
 */

/*
  The builder's two columns: the fields on the left, the live preview on the right.

  Below 1150px they stack, fields first — the preview is the answer and the fields are the
  question, and on a phone the question has to come first.

  The preview column is the smaller of the two and floors at 330px. It holds one thing that knows
  how to shrink, where the left column holds four labelled controls and a row of buttons that do
  not; when there is not enough width to go round, the side that can give should be the one that
  gives.
*/
const BuilderSplit = styled.div`
  display: grid;
  gap: 22px;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;

  @media (min-width: 1150px) {
    grid-template-columns: minmax(0, 1fr) minmax(330px, 0.85fr);
  }
`;

/** The left column, matching the gap the builder's own sections use. */
const BuilderFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const Setup = () => {
  const navigate = useNavigate();
  const {
    blocks,
    setBlocks,
    dirty,
    setDirty,
    template,
    versions,
    loadOlderVersions,
    showVersions,
    saveText,
    restore,
    updateParams,
    refreshPreview,
    preview,
    previewLoading,
    previewVariant,
    previewVariantRequest,
    previewLocale,
    previewLocales,
    setPreviewLocale,
    requestPreviewVariant,
    templates,
    isCampaignTemplate,
    selectedKey,
    filters,
    filtersWereSet,
    audienceMode,
    setAudienceMode,
    channelId,
    channelLabel,
    relationshipScope,
    setChannelAudience,
    channelAudience,
    channels,
    channelsLoading,
    recipientId,
    recipientLabel,
    recipientSearch,
    setRecipientSearch,
    setDirectRecipient,
    directRecipient,
    recipients,
    recipientsLoading,
    policy,
    sendWindow,
    cancel,
    setFilters,
    audience,
    campaignName,
    setCampaignName,
    detailRows,
    setDetailRows,
    rowsReadOnlyReason,
    conversionAction,
    setConversionAction,
    destination,
    setDestination,
    destinationTarget,
    setDestinationTarget,
    challenges,
    challengesLoading,
    challengesHasMore,
    challengeResolved,
    campaignId,
    saveDraft,
    campaigns,
    loadCampaigns,
    stopRecurrence,
    failed,
    loadOk,
    schedule,
    setArchived,
    sendNow,
    testSend,
    testSendResult,
    setRecurrence,
    queued,
    busy,
    isRunning,
    confirmation,
    refusal
  } = useEmailManager();

  /*
    The step badge, from real state rather than a fixed "Step 2 of 3".

    1 until a campaign has a name, 2 while it is unsaved, 3 once a draft exists and can be given a
    time. A hard-coded number would say "Step 2 of 3" to someone who had not started.
  */
  const step = !campaignName ? 1 : campaignId ? 3 : 2;

  /*
    Two preconditions, stated before an operator composes rather than refused after they press send.

    **A zero-recipient audience.** `matching` was available on this page and never checked, so a
    filter set matching nobody could be saved and scheduled — a send to no one, reported as a
    success.

    **The postal address.** The server refuses to schedule or send a campaign while no
    organisation postal address is stored, and this page already holds
    `policy.postalAddressRequirementMet` in context — it simply never read it. So an operator wrote
    a whole campaign and met a generic red toast for a condition the screen already knew about.

    The audience half is now asked of whichever audience mode this campaign actually uses.

    A channel campaign has no filters, so the saved-segment reading of these two lines — "filters
    were set, and they match nobody" — would call every channel campaign audience-less and refuse
    to send it. The channel path asks its own question instead: a channel is chosen, its count has
    come back, and the count says it can send.

    `canSend` rather than `eligibleRecipients > 0`, deliberately. It is false for a count of zero
    *and* for a channel that has gone *and* for a count that could not be produced, which is
    exactly the set of states FR-010, FR-011 and FR-018 ask this page to block on.
  */
  const channelMode = audienceMode === AUDIENCE_MODE.CHANNEL_SPECIFIC;
  const recipientMode = audienceMode === AUDIENCE_MODE.DIRECT_RECIPIENT;
  const channelAudienceReady = Boolean(channelId) && channelAudience.canSend;
  const recipientReady = Boolean(recipientId) && directRecipient.canSend;

  /*
    The audience question, asked of whichever mode this campaign actually uses.

    All three readings are the same shape — was something chosen, and can it be sent to — and they
    have to stay separate because the saved-segment reading ("filters were set, and they match
    nobody") calls every criteria-based campaign audience-less and refuses to send it.

    `canSend` rather than a count comparison, for both criteria modes: it is false for zero, for a
    channel or account that has gone, and for a count that could not be produced, which is exactly
    the set of states this page must block on.
  */
  const noAudience = channelMode
    ? !channelAudienceReady
    : recipientMode
      ? !recipientReady
      : filtersWereSet && audience.total === 0;
  const audienceWasChosen = channelMode
    ? Boolean(channelId)
    : recipientMode
      ? Boolean(recipientId)
      : filtersWereSet;
  const complianceBlocked = policy ? !policy.postalAddressRequirementMet : false;

  /*
    A third precondition, alongside the audience and the postal address.

    Phase 1 points the campaign button at one named challenge, and there is no default challenge to
    fall back on — so a campaign with none chosen has a button that goes nowhere. Checked here rather
    than at the send, because this page already refuses for two other reasons in the same place and
    a fourth kind of refusal in a different corner is how an operator learns to distrust the screen.

    `challengeResolved === null` is the deleted case and blocks too. `undefined` means the lookup has
    not answered yet and blocks nothing.
  */
  const destinationIncomplete =
    destination !== 'fancam' || !destinationTarget || challengeResolved === null;

  /*
    Every required field still empty, from the list the builder marks its fields with.

    Shown by the scheduler all at once, each a link back to its field, where the one-reason-at-a-time
    tooltip above used to be the only explanation. Following a link turns on the builder's field
    messages, so the field it lands on says what it needs.
  */
  const missingFields = missingCampaignFields({
    campaignName,
    destination,
    destinationTarget,
    challengeResolved,
    audienceMode: channelMode || recipientMode ? audienceMode : AUDIENCE_MODE.SAVED_SEGMENT,
    filtersWereSet,
    channelId,
    recipientId
  });
  const [showRequired, setShowRequired] = useState(false);
  const showMissingField = (key) => {
    setShowRequired(true);
    focusRequiredField(key);
  };

  const audienceReady =
    !noAudience &&
    !complianceBlocked &&
    audienceWasChosen &&
    !destinationIncomplete &&
    missingFields.length === 0;

  /*
    Opening a campaign from the list beside the scheduler.

    On the dashboard this was a navigation to this page. From here there is nowhere to go — the
    builder is the card above — so it only moves the parameters the shell already watches, and the
    campaign is adopted into the builder in place. Anchored back to the builder because on a tall
    page the fields that just changed are off-screen above, and a silent update reads as nothing
    having happened.
  */
  const open = (campaign) => {
    const id = campaign.id || campaign._id;
    updateParams(
      campaign.templateKey ? { campaign: id, template: campaign.templateKey } : { campaign: id }
    );
    document.getElementById('builder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /*
    The reason, in the words of whichever audience mode is in force.

    A channel campaign refused for "these filters match nobody" would send an operator to the
    dashboard to widen a filter set the campaign does not have — a true-sounding sentence pointing
    at the wrong control, which is worse than no sentence at all.
  */
  const channelAudienceReason = !channelId
    ? 'No channel chosen. Pick one in the Campaign Builder above.'
    : channelAudience.status === 'loading'
      ? 'Still counting this channel’s eligible fans. Sending is available once the count arrives.'
      : channelAudience.blockReason === AUDIENCE_BLOCK_REASON.CHANNEL_UNAVAILABLE
        ? 'The channel this campaign names can no longer be used. Choose another in the builder above.'
        : channelAudience.blockReason === AUDIENCE_BLOCK_REASON.COUNT_UNAVAILABLE
          ? 'This audience could not be verified, so the campaign cannot be sent yet. Try the builder above again.'
          : 'This channel audience has no eligible fans. Widen the relationship or choose another channel above.';

  /*
    The same four states, said about one person.

    Reworded rather than shared, because every sentence in the channel version points at a control
    this mode does not have, and two of them would be simply untrue: an operator looking at one
    named account does not need to be told to "widen the relationship", and the likeliest cause of
    a blocked single recipient — an address nobody has verified — has no channel equivalent at all.
  */
  const recipientReason = !recipientId
    ? 'No recipient chosen. Pick the account this campaign goes to, in the Campaign Builder above.'
    : directRecipient.status === 'loading'
      ? 'Still checking whether this account can be emailed. Sending is available once the check finishes.'
      : directRecipient.blockReason === AUDIENCE_BLOCK_REASON.RECIPIENT_UNAVAILABLE
        ? 'The account this campaign names can no longer receive email. Choose another in the builder above.'
        : directRecipient.blockReason === AUDIENCE_BLOCK_REASON.COUNT_UNAVAILABLE
          ? 'This recipient could not be verified, so the campaign cannot be sent yet. Try the builder above again.'
          : 'This account cannot be emailed — most often its address has not been verified, or it has unsubscribed. Verify it or choose another account above.';

  const audienceReason = recipientMode
    ? recipientReady
      ? complianceBlocked
        ? 'No organisation postal address is set. Add one under Settings — commercial email cannot be sent without it.'
        : destinationIncomplete
          ? 'No FanCam challenge chosen. Pick the one this campaign sends fans to, in the builder above.'
          : ''
      : recipientReason
    : channelMode
      ? channelAudienceReady
        ? complianceBlocked
          ? 'No organisation postal address is set. Add one under Settings — commercial email cannot be sent without it.'
          : destinationIncomplete
            ? 'No FanCam challenge chosen. Pick the one this campaign sends fans to, in the builder above.'
            : ''
        : channelAudienceReason
      : !filtersWereSet
        ? 'No audience set. Choose one on the Email Manager dashboard first.'
        : noAudience
          ? 'These filters match nobody. Widen them on the Email Manager dashboard before sending.'
          : complianceBlocked
            ? 'No organisation postal address is set. Add one under Settings — commercial email cannot be sent without it.'
            : challengeResolved === null
              ? 'The FanCam challenge this campaign points at no longer exists. Choose another in the builder above.'
              : destination !== 'fancam'
                ? 'No button destination chosen. Pick the FanCam challenge this campaign sends fans to, in the builder above.'
                : !destinationTarget
                  ? 'No FanCam challenge chosen. Pick the one this campaign sends fans to, in the builder above.'
                  : '';

  /*
    The page's only preview, extracted so it can stand in either of two places.

    For the campaign template it is the right-hand half of the builder, beside the fields that
    decide what it shows — where the design puts it. For the other eight there are no such fields,
    so it takes the whole card rather than sitting in a column beside an empty one.
  */
  const previewPane = (
    <div id="preview" style={{ minWidth: 0 }}>
      {/*
        A failed render, said where the stale email is.

        The pane keeps the previous email on screen while a new one is fetched, which is what
        makes typing bearable — and is exactly what makes a silent failure dangerous: an
        operator would go on reading an email that no longer matches the fields, with nothing
        to say so. `markLoad('preview', …)` already recorded the failure and nobody read it.
      */}
      {failed('preview') ? (
        <Note style={{ color: T.red }}>
          This preview could not be rendered, so what you see below is the last one that worked —
          not your current text. Press Refresh preview to try again.
        </Note>
      ) : null}
      <CampaignPreview
        preview={preview}
        previewLoading={previewLoading}
        previewVariant={previewVariant}
        previewVariantRequest={previewVariantRequest}
        previewLocale={previewLocale}
        previewLocales={previewLocales}
        onPreviewLocaleChange={setPreviewLocale}
        onRefreshPreview={refreshPreview}
        /*
          The subject block, not the title.

          These are two different fields and the editor above has split them into two inputs for
          exactly one reason: the value in `title` goes into the email's `<h1>` and the value in
          `subject` goes into the inbox. This pane labels what it shows "Subject:", so passing the
          title reproduced the defect the split was made to fix — an administrator reads a subject
          line here that is not the one that will be sent.

          Falls back to the title for a template version stored before the split, which had no
          `subject` block at all; showing the h1 there is wrong in the same way, but showing an
          empty subject line would be wrong and blank.
        */
        subject={blocks?.subject || blocks?.title}
      />
    </div>
  );

  return (
    <>
      <PageBand
        brand
        title="Email Manager Dashboard — Setup & Automation"
        subtitle="Configure rich templates, customize trigger-ready campaigns, and organize scheduled dispatches"
        actions={
          <>
            <Btn variant="outline" onClick={() => navigate('/admin/email')}>
              Back to dashboard
            </Btn>
            <Btn variant="outline" icon="Gear" onClick={() => navigate('/admin/email/settings')}>
              Settings
            </Btn>
            {/*
              Save Workspace was here, disabled, as a placeholder for bundling template edits,
              filters and a schedule under one name. Removed at the owner's request: each of the
              three saves on its own, and the decision is recorded in the Settings note.
            */}
          </>
        }
      />

      <Card id="editor">
        <SectionHead
          icon="Pencil"
          title="Template Editor (Editable Fields)"
          right={<DraftBadge dirty={dirty} version={template?.currentVersionNumber} />}
        />

        <TemplateTextEditor
          template={template}
          blocks={blocks}
          editableBlocks={template?.editableBlocks || []}
          footerText={policy?.complianceFooterText}
          busy={busy}
          saving={isRunning('save-text')}
          dirty={dirty}
          onChange={(next) => {
            setBlocks(next);
            setDirty(true);
          }}
          onSave={saveText}
          /*
            Preview renders into the pane in the builder below rather than navigating anywhere.
            It used to send an operator to the composer, which is a campaign builder — for the
            eight templates that cannot be sent to an audience that was a page about something
            else.
          */
          onPreview={(variant) => {
            requestPreviewVariant(variant);
            refreshPreview();
            /*
              Scrolled to, because the pane is in the next card down.

              The preview moved into the Campaign Builder, so on an ordinary laptop it is below the
              fold when these buttons are pressed. Switching a frame the operator cannot see reads
              exactly like the button doing nothing — which is what it did before it switched
              anything at all.
            */
            document.getElementById('preview')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }}
          onShowVersions={() => updateParams({ versions: showVersions ? null : '1' })}
        />

        {/*
          The server's refusal, next to the editor it refused.

          `updateTemplateText` rejects a block that is not editable on this template and names it;
          that was a toast at the top-right of the window, three screens from the field.
        */}
        {refusal('save-text') ? <Note style={{ color: T.red }}>{refusal('save-text')}</Note> : null}

        {showVersions ? (
          <VersionHistory
            versions={versions.versions}
            onRestore={restore}
            onLoadMore={loadOlderVersions}
            hasMore={!!versions.nextCursor}
            busy={busy}
          />
        ) : null}

        {/*
          A note stood here telling operators their edits were not sent on every keystroke and that
          Preview was how to see them. Both halves stopped being true: the preview follows the
          fields now, settling shortly after typing stops. Removed rather than reworded — an
          instruction for a thing that happens by itself is a thing to read and then not do, and the
          Preview buttons above still say what they do on their own faces.

          Save Template is unaffected and still the only thing that keeps an edit.
        */}
      </Card>

      {/*
        The campaign half of this page, which eight of the nine templates do not have.

        A welcome email, a verification and a password reset are fired by an account event. They
        have no audience, no schedule, no detail card, no conversion action and no per-campaign
        destination — and every one of those controls was rendered for them anyway, because this
        page was written for the one template that has them while the route accepts a `?template=`
        for any of the nine. An operator opening Password Reset from the catalog met a Campaign
        Builder asking who to send it to.

        Hidden rather than disabled. A greyed-out audience picker on a password-reset email still
        says "this email has an audience, you just cannot set it here", which is the opposite of
        true — and the read-only fields in the editor above are marked that way precisely because
        they *do* belong to the template and are decided elsewhere. These do not belong at all.
      */}
      <Card id="builder">
        <SectionHead
          icon={isCampaignTemplate ? 'Layers' : 'Mail'}
          title={isCampaignTemplate ? 'Campaign Builder' : 'Template Preview'}
          right={
            isCampaignTemplate ? (
              <Badge tone="indigo">{`Step ${step} of 3`}</Badge>
            ) : (
              <Badge tone="slate">Sent on an account event</Badge>
            )
          }
        />

        {isCampaignTemplate ? null : (
          <Note>
            This email is sent automatically when the account event it belongs to happens, so there
            is nothing here to aim or schedule. Edit its wording in the Template Editor above — the
            preview updates as you type.
          </Note>
        )}

        {isCampaignTemplate ? (
          <BuilderSplit>
            <BuilderFields>
              <CampaignBuilder
                templates={templates}
                selectedKey={selectedKey}
                onSelectTemplate={(key) => updateParams({ template: key })}
                campaignName={campaignName}
                filtersWereSet={filtersWereSet}
                onCampaignName={setCampaignName}
                detailRows={detailRows}
                onDetailRows={setDetailRows}
                rowsReadOnlyReason={rowsReadOnlyReason}
                conversionAction={conversionAction}
                destination={destination}
                onDestination={setDestination}
                destinationTarget={destinationTarget}
                onDestinationTarget={setDestinationTarget}
                challenges={challenges}
                challengesLoading={challengesLoading}
                challengesHasMore={challengesHasMore}
                challengeResolved={challengeResolved}
                onConversionAction={setConversionAction}
                filters={filters}
                audienceMode={audienceMode}
                onAudienceMode={setAudienceMode}
                channels={channels}
                channelId={channelId}
                channelLabel={channelLabel}
                channelsLoading={channelsLoading}
                relationshipScope={relationshipScope}
                onChannelAudience={setChannelAudience}
                channelAudience={channelAudience}
                recipients={recipients}
                recipientSearch={recipientSearch}
                onRecipientSearch={setRecipientSearch}
                recipientsLoading={recipientsLoading}
                recipientId={recipientId}
                recipientLabel={recipientLabel}
                onDirectRecipient={setDirectRecipient}
                directRecipient={directRecipient}
                onSegmentShortcut={(segment) =>
                  setFilters({ ...filters, segments: segment ? [segment] : undefined })
                }
                audienceTotal={audience.audienceTotal}
                matching={audience.total}
                onSaveDraft={saveDraft}
                campaignStatus={campaigns.find((c) => c.id === campaignId)?.status}
                campaignId={campaignId}
                busy={busy}
                isRunning={isRunning}
                showRequired={showRequired}
              />

              {/*
              The server's own words, beside the button that provoked them.

              These arrived as a red toast in the corner: "This campaign is scheduled, not a
              draft" is precisely the sentence an operator needs, and it was shown at the wrong
              end of the page for six seconds. `updateCampaign` names the status it found, so the
              text is worth keeping.
            */}
              {refusal('save-draft') ? (
                <Note style={{ color: T.red }}>{refusal('save-draft')}</Note>
              ) : null}

              {/*
              Said in the card the button is in.

              Duplicate and Archive both refresh the campaign list, which is beside the scheduler
              below — so from here they completed with no visible effect whatsoever, and the only
              signal was a button label reverting.
            */}
              {['duplicate', 'archive'].map((action) =>
                confirmation(action) ? (
                  <Note key={action} style={{ color: T.green }}>
                    {confirmation(action)}
                  </Note>
                ) : null
              )}
            </BuilderFields>

            {previewPane}
          </BuilderSplit>
        ) : (
          previewPane
        )}
      </Card>

      {isCampaignTemplate ? (
        <Card id="schedule">
          <SectionHead
            icon="Calendar"
            title="Email Scheduling & Automations"
            right={
              <Badge tone={queued > 0 ? 'green' : 'slate'}>
                {queued > 0
                  ? `${queued} dispatch${queued === 1 ? '' : 'es'} queued`
                  : 'Nothing queued'}
              </Badge>
            }
          />
          <ScheduleCalendar
            onSchedule={schedule}
            onSendNow={sendNow}
            onTestSend={testSend}
            onSetRecurrence={setRecurrence}
            onCancel={cancel}
            testSendResult={testSendResult}
            sendWindow={sendWindow}
            /*
            The count the scheduler shows, from the audience the campaign actually uses.

            It read `audience.total` unconditionally, which for a channel campaign is the size of
            the whole unfiltered platform — the one number that must never appear beside a Send
            button for a campaign aimed at one channel's followers.
          */
            matching={
              channelMode
                ? (channelAudience.eligibleRecipients ?? 0)
                : recipientMode
                  ? (directRecipient.eligibleRecipients ?? 0)
                  : audience.total
            }
            audienceReady={audienceReady}
            audienceReason={audienceReason}
            // Criteria-based campaigns only — the server refuses to schedule one whose audience is
            // empty or whose channel or recipient has gone, so the button should not offer to try.
            audienceBlocksSchedule={
              (channelMode && !channelAudienceReady) || (recipientMode && !recipientReady)
            }
            missingFields={missingFields}
            onShowMissingField={showMissingField}
            policy={policy}
            onEditPolicy={() => navigate('/admin/email/settings')}
            busy={busy}
            isRunning={isRunning}
            canSchedule={!!campaignId}
            reason="Save a campaign draft in the builder above first — a campaign has to exist before it can be given a time."
            /*
            The column beside the controls.

            Three states, not one. `campaigns` starts as `[]`, so rendering the list
            unconditionally flashed "No campaigns yet" before the fetch resolved — and a failed
            fetch rendered the same thing, which said something false about an operator's own
            work.
          */
            aside={
              failed('campaigns') ? (
                <Failed
                  title="The campaigns could not be loaded"
                  onRetry={loadCampaigns}
                  busy={busy}
                >
                  This is not an empty list — the request itself failed.
                </Failed>
              ) : loadOk.campaigns === undefined ? (
                <Loading title="Loading campaigns…" />
              ) : (
                <CampaignList
                  campaigns={campaigns}
                  onOpen={open}
                  onArchive={setArchived}
                  onCancel={cancel}
                  onStopRecurrence={stopRecurrence}
                  busy={busy}
                />
              )
            }
          />

          {['schedule', 'set-recurrence', 'send-now'].map((action) =>
            refusal(action) ? (
              <Note key={`${action}-refusal`} style={{ color: T.red }}>
                {refusal(action)}
              </Note>
            ) : null
          )}

          {['schedule', 'set-recurrence'].map((action) =>
            confirmation(action) ? (
              <Note key={action} style={{ color: T.green }}>
                {confirmation(action)}
              </Note>
            ) : null
          )}
        </Card>
      ) : null}
    </>
  );
};

export default Setup;
